import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormNumber,
  FormRoot,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { format } from "date-fns";
import { useState } from "react";
import { z } from "zod";
import {
  type PublicOutstandingRoomBookingItem,
  publicBookingApi,
} from "../api/client";

const LookupSchema = z
  .object({
    contactPhone: z.string().min(1, "ກະລຸນາໃສ່ເບີໂທ"),
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    bookingId: z.string().optional(),
  })
  .refine(
    (d) =>
      (d.bookingId != null && d.bookingId.trim().length > 0) || d.date != null,
    {
      message: "ກະລຸນາໃສ່ວັນທີ ຫຼື ລະຫັດການຈອງ",
      path: ["date"],
    },
  );

const PaySchema = z.object({
  contactPhone: z.string().min(1),
  amount: z.coerce.number().int().positive(),
  notes: z.string().optional(),
});

type LookupValues = z.infer<typeof LookupSchema>;
type PayValues = z.infer<typeof PaySchema>;

export function StudioPayBalancePanel() {
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<PublicOutstandingRoomBookingItem[]>([]);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(
    null,
  );

  const lookupMethods = RHF.useForm<LookupValues>({
    resolver: zodResolver(LookupSchema),
    defaultValues: {
      contactPhone: "",
      date: format(new Date(), "yyyy-MM-dd"),
      bookingId: "",
    },
  });

  const payMethods = RHF.useForm<PayValues>({
    resolver: zodResolver(PaySchema),
    defaultValues: { contactPhone: "", amount: 0, notes: "" },
  });

  const selected = items.find((i) => i.bookingId === selectedBookingId);

  const onLookup = async (vals: LookupValues) => {
    setError(null);
    setSelectedBookingId(null);
    setItems([]);
    try {
      const res = await publicBookingApi.lookupOutstandingRoomBookings({
        contactPhone: vals.contactPhone,
        date: vals.date,
        bookingId: vals.bookingId?.trim() || undefined,
      });
      setItems(res.items);
      if (res.items.length === 0) {
        setError("ບໍ່ພົບການຈອງທີ່ຄ້າງເງິນຕາມເງື່ອນໄຂນີ້");
      }
      payMethods.setValue("contactPhone", vals.contactPhone);
    } catch (e) {
      setError(e instanceof Error ? e.message : "ຄົ້ນຫາບໍ່ສຳເລັດ");
    }
  };

  const onPay = async (vals: PayValues) => {
    if (!selectedBookingId) return;
    setError(null);
    try {
      await publicBookingApi.payRoomBookingBalance(selectedBookingId, {
        contactPhone: vals.contactPhone,
        amount: vals.amount,
        notes: vals.notes?.trim() || undefined,
      });
      toast.success("ບັນທຶກການຊຳລະແລ້ວ");
      const lookupVals = lookupMethods.getValues();
      const res = await publicBookingApi.lookupOutstandingRoomBookings({
        contactPhone: lookupVals.contactPhone,
        date: lookupVals.date,
        bookingId: lookupVals.bookingId?.trim() || undefined,
      });
      setItems(res.items);
      setSelectedBookingId(null);
      payMethods.reset({
        contactPhone: lookupVals.contactPhone,
        amount: 0,
        notes: "",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "ຊຳລະບໍ່ສຳເລັດ");
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="font-medium text-base">
            ຄົ້ນຫາການຈອງທີ່ຄ້າງເງິນ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FormRoot methods={lookupMethods} onSubmit={onLookup}>
            <div className="flex flex-col gap-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <FormInput
                name="contactPhone"
                label="ເບີໂທຜູ້ຈອງ"
                requiredMark
                placeholder="ຕົວຢ່າງ: 020…"
              />
              <FormInput name="date" label="ວັນທີຈອງ" type="date" />
              <FormInput
                name="bookingId"
                label="ລະຫັດການຈອງ (ທາງເລືອກ)"
                placeholder="ຄົ້ນຫາເມື່ອຮູ້ລະຫັດ"
              />
              <p className="text-muted-foreground text-xs">
                ໃສ່ວັນທີ ຫຼື ລະຫັດການຈອງຢ່າງໜ້ອຍໜຶ່ງຢ່າງ ຄູ່ກັບເບີໂທ.
              </p>
              <Button
                type="submit"
                isLoading={lookupMethods.formState.isSubmitting}
              >
                ຄົ້ນຫາ
              </Button>
            </div>
          </FormRoot>
        </CardContent>
      </Card>

      {items.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="font-medium text-base">ຜົນຄົ້ນຫາ</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {items.map((row) => (
              <button
                key={row.bookingId}
                type="button"
                onClick={() => {
                  setSelectedBookingId(row.bookingId);
                  payMethods.setValue("amount", row.remainingAmount);
                }}
                className={`rounded-md border p-3 text-left text-sm transition-colors ${
                  selectedBookingId === row.bookingId
                    ? "border-primary bg-muted/40"
                    : "border-border hover:bg-muted/20"
                }`}
              >
                <div className="font-medium">
                  {row.roomName ?? "ຫ້ອງ"} · {row.date}{" "}
                  {row.startTime?.slice(0, 5)} – {row.endTime?.slice(0, 5)}
                </div>
                <div className="text-muted-foreground text-xs">
                  {row.contactName ?? "—"} · ຄ້າງ{" "}
                  {row.remainingAmount.toLocaleString("lo-LA")} ກີບ
                </div>
              </button>
            ))}

            {selected ? (
              <FormRoot methods={payMethods} onSubmit={onPay}>
                <div className="mt-2 flex flex-col gap-4 border-border border-t pt-4">
                  <FormInput
                    name="contactPhone"
                    label="ເບີໂທ (ຢືນຢັນ)"
                    readOnly
                    className="bg-muted/30"
                  />
                  <FormNumber
                    name="amount"
                    label="ຈຳນວນທີ່ຮັບ (ກີບ)"
                    min={1}
                    max={selected.remainingAmount}
                    step={1}
                    requiredMark
                  />
                  <FormInput name="notes" label="ໝາຍເຫດ (ທາງເລືອກ)" />
                  <Button
                    type="submit"
                    isLoading={payMethods.formState.isSubmitting}
                  >
                    ບັນທຶກການຊຳລະ
                  </Button>
                </div>
              </FormRoot>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
